import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Sun, Moon, Trophy, User2, Sparkles, ClipboardList, Clock, Lightbulb, Send, Share2, Copy, CheckCircle2, Circle, Crown, TrendingUp, Eye, EyeOff } from "lucide-react";
import ETSGauge from "../components/ETSGauge";
import BroadcastModal from "../components/BroadcastModal";
import "./employee.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("tr_employee_token");
  return { headers: { Authorization: `Bearer ${t}` } };
}

export default function EmployeeDashboard() {
  const nav = useNavigate();
  const [dark, setDark] = useState(true);
  const [me, setMe] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [innovations, setInnovations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [topReferrers, setTopReferrers] = useState(null);
  const [topPeriod, setTopPeriod] = useState("quarter");
  const [broadcasts, setBroadcasts] = useState([]);
  const [innTitle, setInnTitle] = useState("");
  const [innDesc, setInnDesc] = useState("");
  const [innBusy, setInnBusy] = useState(false);
  const [clockBusy, setClockBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [{ data: meDoc }, { data: lb }, { data: att }, { data: inns }, { data: tks }, { data: refs }, { data: topRefs }] = await Promise.all([
        axios.get(`${API}/employee/me`, authHeaders()),
        axios.get(`${API}/employee/leaderboard?limit=10`, authHeaders()),
        axios.get(`${API}/employee/attendance/today`, authHeaders()),
        axios.get(`${API}/employee/me/innovations`, authHeaders()),
        axios.get(`${API}/employee/me/tasks?status=all`, authHeaders()),
        axios.get(`${API}/employee/me/referrals`, authHeaders()),
        axios.get(`${API}/employee/referrers/leaderboard?period=${topPeriod}&limit=10`, authHeaders()),
      ]);
      setMe(meDoc);
      setLeaderboard(lb || []);
      setAttendance(att);
      setInnovations(inns || []);
      setTasks(tks || []);
      setReferrals(refs);
      setTopReferrers(topRefs);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("tr_employee_token");
        localStorage.removeItem("tr_employee");
        nav("/employee/login", { replace: true });
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally { setLoading(false); }
  }, [nav, topPeriod]);

  useEffect(() => {
    if (!localStorage.getItem("tr_employee_token")) { nav("/employee/login", { replace: true }); return; }
    load();
  }, [load, nav]);

  useEffect(() => {
    // Fetch active broadcasts for employees once on dashboard mount
    axios.get(`${API}/broadcasts/active?audience=employees`)
      .then(r => setBroadcasts(r.data || []))
      .catch(() => {});
  }, []);

  const logout = async () => {
    try { await axios.post(`${API}/employee/auth/logout`, {}, authHeaders()); }
    catch (e) { console.warn("Employee logout API failed (proceeding with local cleanup):", e); }
    localStorage.removeItem("tr_employee_token");
    localStorage.removeItem("tr_employee");
    nav("/employee/login", { replace: true });
  };

  const clockIn = async () => {
    setClockBusy(true);
    try { await axios.post(`${API}/employee/attendance/clock-in`, {}, authHeaders()); toast.success("Clocked in — +4 attendance"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setClockBusy(false); }
  };
  const clockOut = async () => {
    setClockBusy(true);
    try { const { data } = await axios.post(`${API}/employee/attendance/clock-out`, {}, authHeaders()); toast.success(`Clocked out (${data.minutes} min)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setClockBusy(false); }
  };

  const submitInnovation = async () => {
    if (!innTitle.trim() || !innDesc.trim()) { toast.error("Title and description required"); return; }
    setInnBusy(true);
    try {
      await axios.post(`${API}/employee/innovation/submit`, { title: innTitle, description: innDesc }, authHeaders());
      toast.success("Idea submitted — admin will review");
      setInnTitle(""); setInnDesc(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setInnBusy(false); }
  };

  const completeTask = async (tid) => {
    try {
      const { data } = await axios.patch(`${API}/employee/me/tasks/${tid}/complete`, {}, authHeaders());
      toast.success(`Task done · +${data.points_awarded} productivity`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyReferralLink = () => {
    if (!referrals?.employee_referral_code) return;
    const link = `${window.location.origin}/signup?ref=${referrals.employee_referral_code}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => toast.success("Referral link copied"))
        .catch(() => toast.info(`Copy blocked. Link: ${link}`));
    } else {
      toast.info(`Referral link: ${link}`);
    }
  };

  if (loading || !me) {
    return (
      <div className={`emp-scope ${dark ? "emp-dark" : ""} flex items-center justify-center min-h-screen`}>
        <p className="emp-eyebrow" data-testid="emp-dash-loading">Loading dashboard…</p>
      </div>
    );
  }

  const b = me.ets_breakdown || {};
  const kpis = [
    { label: "Sales", value: b.sales || 0, max: 300, icon: "₹" },
    { label: "Customer Exp.", value: b.customer_experience || 0, max: 200, icon: "★" },
    { label: "Productivity", value: b.productivity || 0, max: 150, icon: "⚡" },
    { label: "Attendance", value: b.attendance || 0, max: 100, icon: "✓" },
    { label: "Behaviour", value: b.behaviour || 0, max: 100, icon: "◎" },
    { label: "Ownership", value: b.ownership || 0, max: 100, icon: "▲" },
    { label: "Learning", value: b.learning || 0, max: 50, icon: "◆" },
    { label: "Innovation", value: b.innovation || 0, max: 50, icon: "✧" },
  ];

  const myRank = leaderboard.findIndex(l => l.id === me.id) + 1;

  return (
    <div className={`emp-scope ${dark ? "emp-dark" : ""} min-h-screen relative`}>
      {me && (
        <BroadcastModal broadcasts={broadcasts} storageKey={`tr_bc_emp_${me.id}`} />
      )}
      {/* Header */}
      <header className="emp-glass sticky top-0 z-40 border-l-0 border-r-0 border-t-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-current opacity-10 flex items-center justify-center">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} />
            </div>
            <div>
              <p className="emp-eyebrow" style={{ opacity: 0.6 }}>TileRoom</p>
              <p className="text-sm font-medium">Employee Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setDark(d => !d)} className="emp-btn-ghost inline-flex items-center gap-1.5" data-testid="emp-dash-theme">
              {dark ? <Sun className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {dark ? "Light" : "Dark"}
            </button>
            <button onClick={logout} className="emp-btn-ghost inline-flex items-center gap-1.5" data-testid="emp-dash-logout">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome + gauge */}
        <div className="emp-fade-in grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="emp-glass p-8 lg:col-span-2">
            <p className="emp-eyebrow mb-2">Welcome back</p>
            <h1 className="font-editorial text-3xl sm:text-5xl leading-none mb-4">{me.name}</h1>
            <p className="text-sm mb-6" style={{ color: "var(--emp-muted)" }}>
              <span className="emp-eyebrow" style={{ color: "var(--emp-accent)" }}>{me.employee_code}</span>
              <span className="mx-2 opacity-40">·</span>
              {me.designation || "Team Member"}
              <span className="mx-2 opacity-40">·</span>
              {(me.departments || []).join(", ") || "—"}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`emp-grade emp-grade-${(me.grade || "Bronze").replace(/\s/g, "")}`} data-testid="emp-grade-badge">{me.grade}</span>
              <span className="text-xs" style={{ color: "var(--emp-muted)" }}>
                <Trophy className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.5} />
                {myRank > 0 ? `Rank #${myRank} in top 10` : "Keep going to reach top 10"}
              </span>
            </div>
          </div>

          <div className="emp-glass p-6 flex items-center justify-center">
            <ETSGauge score={me.ets_score} size={200} stroke={14} />
          </div>
        </div>

        {/* KPI grid */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="emp-kpi-grid">
          {kpis.map((k) => {
            const pct = k.max ? Math.min(100, Math.round((k.value / k.max) * 100)) : 0;
            return (
              <div key={k.label} className="emp-glass p-4 emp-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <p className="emp-eyebrow">{k.label}</p>
                  <span style={{ color: "var(--emp-accent)", fontSize: 12 }}>{k.icon}</span>
                </div>
                <p className="text-2xl font-editorial leading-none">{k.value}<span className="text-xs opacity-40"> / {k.max}</span></p>
                <div className="mt-2 h-1 w-full bg-current opacity-10">
                  <div className="h-full transition-all duration-1000" style={{ width: `${pct}%`, background: "var(--emp-accent)" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Top Referrers · public strip */}
        {topReferrers && topReferrers.top_referrers.length > 0 && (
          <div className="mt-6 emp-glass p-6 emp-fade-in" data-testid="emp-top-referrers">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <p className="emp-eyebrow inline-flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} />
                  Top Referrers · driving revenue via Refer-a-Buyer
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>
                  ₹{Number(topReferrers.totals.period_revenue || 0).toLocaleString("en-IN")} referred revenue across {topReferrers.totals.period_orders} order{topReferrers.totals.period_orders === 1 ? "" : "s"} · {topReferrers.totals.employees_with_referrals} employee{topReferrers.totals.employees_with_referrals === 1 ? "" : "s"} contributing
                </p>
              </div>
              <div className="flex gap-1" data-testid="emp-top-period">
                {[
                  { k: "month", label: "1M" },
                  { k: "quarter", label: "1Q" },
                  { k: "year", label: "1Y" },
                  { k: "all", label: "ALL" },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    onClick={() => setTopPeriod(k)}
                    className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${topPeriod === k ? "" : "opacity-50 hover:opacity-100"}`}
                    style={topPeriod === k
                      ? { background: "var(--emp-accent)", color: "var(--emp-primary)", borderColor: "var(--emp-accent)" }
                      : { borderColor: "var(--emp-border)" }}
                    data-testid={`emp-top-period-${k}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="emp-top-referrers-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--emp-border)" }}>
                    <th className="text-left p-2 emp-eyebrow" style={{ width: 50 }}>Rank</th>
                    <th className="text-left p-2 emp-eyebrow">Employee</th>
                    <th className="text-right p-2 emp-eyebrow">Revenue Driven</th>
                    <th className="text-right p-2 emp-eyebrow hidden sm:table-cell">Orders</th>
                    <th className="text-right p-2 emp-eyebrow hidden md:table-cell">Buyers</th>
                    <th className="text-right p-2 emp-eyebrow">Bonus ETS</th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrers.top_referrers.map((r) => {
                    const isMe = r.employee_id === me.id;
                    const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`;
                    return (
                      <tr
                        key={r.employee_id}
                        className="border-b transition-colors"
                        style={{ borderColor: "var(--emp-border)", background: isMe ? "rgba(201,169,104,0.08)" : "transparent" }}
                        data-testid={`emp-top-row-${r.employee_id}`}
                      >
                        <td className="p-2" style={{ fontSize: r.rank <= 3 ? 18 : 12 }}>{medal}</td>
                        <td className="p-2">
                          <p className={`text-sm ${isMe ? "font-medium" : ""}`}>
                            {r.name}
                            {isMe && <span className="emp-eyebrow ml-1" style={{ color: "var(--emp-accent)" }}>· You</span>}
                            {r.rank === 1 && <Crown className="w-3.5 h-3.5 inline ml-1" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} />}
                          </p>
                          <p className="text-[10px]" style={{ color: "var(--emp-muted)" }}>{r.employee_code} · {r.designation || "—"}</p>
                        </td>
                        <td className="p-2 text-right font-editorial" style={{ color: "var(--emp-accent)" }}>
                          ₹{Number(r.revenue || 0).toLocaleString("en-IN")}
                        </td>
                        <td className="p-2 text-right hidden sm:table-cell">{r.orders}</td>
                        <td className="p-2 text-right hidden md:table-cell">{r.active_buyers}/{r.referred_total}</td>
                        <td className="p-2 text-right font-medium" style={{ color: r.bonus_ets > 0 ? "#10B981" : "var(--emp-muted)" }}>
                          +{r.bonus_ets}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {topReferrers.me && !topReferrers.top_referrers.find(r => r.employee_id === me.id) && (
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "var(--emp-border)", color: "var(--emp-muted)" }} data-testid="emp-top-my-rank">
                Your rank: <strong style={{ color: "var(--emp-accent)" }}>#{topReferrers.me.rank}</strong> · ₹{Number(topReferrers.me.revenue).toLocaleString("en-IN")} driven · +{topReferrers.me.bonus_ets} bonus ETS
              </div>
            )}
          </div>
        )}

        {/* Bottom row: attendance + innovation + leaderboard */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="emp-glass p-6 lg:col-span-3 emp-fade-in space-y-6">
            {/* Attendance card */}
            <div className="pb-6 border-b" style={{ borderColor: "var(--emp-border)" }}>
              <div className="flex items-center justify-between">
                <p className="emp-eyebrow inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Attendance</p>
                <span className="emp-eyebrow" style={{ opacity: 0.5 }}>{new Date().toDateString()}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {!attendance?.clock_in_at ? (
                  <button onClick={clockIn} disabled={clockBusy} className="emp-btn-primary" data-testid="emp-clock-in">Clock In</button>
                ) : !attendance?.clock_out_at ? (
                  <>
                    <span className="text-xs" style={{ color: "var(--emp-muted)" }}>In at {new Date(attendance.clock_in_at).toLocaleTimeString()}</span>
                    <button onClick={clockOut} disabled={clockBusy} className="emp-btn-ghost" data-testid="emp-clock-out">Clock Out</button>
                  </>
                ) : (
                  <span className="text-xs" style={{ color: "var(--emp-muted)" }}>
                    In {new Date(attendance.clock_in_at).toLocaleTimeString()} · Out {new Date(attendance.clock_out_at).toLocaleTimeString()} · <strong>{attendance.minutes} min</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Today's Tasks */}
            <div className="pb-6 border-b" style={{ borderColor: "var(--emp-border)" }}>
              <div className="flex items-center justify-between mb-3">
                <p className="emp-eyebrow inline-flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Today's Tasks · {tasks.filter(t => t.status === "pending").length} pending</p>
              </div>
              {tasks.length === 0 ? (
                <p className="text-xs" style={{ color: "var(--emp-muted)" }}>No tasks assigned yet. Your manager can assign tasks from Admin → Employees → Tasks.</p>
              ) : (
                <ul className="space-y-2" data-testid="emp-tasks-list">
                  {tasks.slice(0, 8).map(t => (
                    <li key={t.id} className={`flex items-center gap-3 py-2 px-2 ${t.status === "done" ? "opacity-50" : ""}`} data-testid={`emp-task-${t.id}`}>
                      <button
                        onClick={() => t.status === "pending" && completeTask(t.id)}
                        disabled={t.status === "done"}
                        className="flex-shrink-0"
                        data-testid={`emp-task-check-${t.id}`}
                        style={{ background: "transparent", border: "none", cursor: t.status === "pending" ? "pointer" : "default" }}
                      >
                        {t.status === "done" ? <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} style={{ color: "#10B981" }} /> : <Circle className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--emp-muted)" }} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${t.status === "done" ? "line-through" : ""}`}>{t.title}</p>
                        {t.description && <p className="text-[10px] mt-0.5 truncate" style={{ color: "var(--emp-muted)" }}>{t.description}</p>}
                      </div>
                      <span className={`emp-grade emp-grade-${t.priority === "urgent" ? "NeedsImprovement" : t.priority === "high" ? "Gold" : t.priority === "low" ? "Silver" : "Bronze"}`}>{t.priority}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Innovation submit */}
            <div>
              <p className="emp-eyebrow inline-flex items-center gap-1.5 mb-3"><Lightbulb className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Innovation · submit an idea</p>
              <input className="emp-input mb-2" placeholder="One-line title" value={innTitle} onChange={(e) => setInnTitle(e.target.value)} data-testid="emp-inn-title" />
              <textarea rows={3} className="emp-input mb-2 resize-none" placeholder="How does it improve TileRoom?" value={innDesc} onChange={(e) => setInnDesc(e.target.value)} data-testid="emp-inn-desc" />
              <button onClick={submitInnovation} disabled={innBusy} className="emp-btn-primary inline-flex items-center gap-2" data-testid="emp-inn-submit">
                <Send className="w-3 h-3" strokeWidth={1.5} /> {innBusy ? "Submitting…" : "Submit"}
              </button>
              {innovations.length > 0 && (
                <ul className="mt-4 text-xs space-y-1">
                  {innovations.slice(0, 5).map(inn => (
                    <li key={inn.id} className="flex items-center justify-between">
                      <span className="truncate mr-2">{inn.title}</span>
                      <span className={`emp-grade emp-grade-${inn.status === "approved" ? "Gold" : inn.status === "rejected" ? "NeedsImprovement" : "Silver"}`}>{inn.status}{inn.status === "approved" && ` +${inn.points_awarded}`}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 emp-fade-in space-y-6">
            {/* Refer-a-Buyer */}
            {referrals && (
              <div className="emp-glass p-6" data-testid="emp-referral-card">
                <div className="flex items-center justify-between mb-3">
                  <p className="emp-eyebrow inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Refer a Buyer</p>
                </div>
                <p className="text-xs mb-3" style={{ color: "var(--emp-muted)" }}>Share your unique link — earn <strong style={{ color: "var(--emp-accent)" }}>+1 sales ETS per ₹10,000</strong> (cap 15 per order) whenever your referred buyer places any order. Forever.</p>
                <div className="mb-3">
                  <p className="text-[10px] emp-eyebrow mb-1" style={{ opacity: 0.6 }}>Your referral code</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm px-2 py-1 flex-1" style={{ background: "var(--emp-primary)", color: "var(--emp-accent)", borderRadius: 4, fontFamily: "monospace" }} data-testid="emp-ref-code">{referrals.employee_referral_code}</code>
                    <button onClick={copyReferralLink} className="emp-btn-ghost inline-flex items-center gap-1 text-xs" data-testid="emp-ref-copy"><Copy className="w-3 h-3" strokeWidth={1.5} /> Copy Link</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="border p-3" style={{ borderColor: "var(--emp-border)" }}>
                    <p className="emp-eyebrow" style={{ opacity: 0.6 }}>Referred</p>
                    <p className="text-2xl font-editorial" data-testid="emp-ref-count">{referrals.referred_buyers_count}</p>
                  </div>
                  <div className="border p-3" style={{ borderColor: "var(--emp-border)" }}>
                    <p className="emp-eyebrow" style={{ opacity: 0.6 }}>Bonus ETS</p>
                    <p className="text-2xl font-editorial" style={{ color: "var(--emp-accent)" }} data-testid="emp-ref-points">+{referrals.total_bonus_points}</p>
                  </div>
                </div>
                {referrals.referred_buyers?.length > 0 && (
                  <ul className="mt-3 text-xs space-y-1 max-h-32 overflow-y-auto" data-testid="emp-ref-list">
                    {referrals.referred_buyers.slice(0, 5).map(b => (
                      <li key={b.id} className="flex items-center justify-between">
                        <span className="truncate mr-2">{b.company_name || b.person_name}</span>
                        <span className="emp-eyebrow" style={{ opacity: 0.6 }}>{b.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Leaderboard */}
            <div className="emp-glass p-6" data-testid="emp-leaderboard">
            <div className="flex items-center justify-between mb-4">
              <p className="emp-eyebrow inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Leaderboard · Top 10</p>
            </div>
            <ul className="space-y-2">
              {leaderboard.map((l, i) => {
                const isMe = l.id === me.id;
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
                return (
                  <li key={l.id} className={`flex items-center gap-3 py-2 px-2 ${isMe ? "bg-current/5" : ""}`}>
                    <span className="w-8 text-center" style={{ fontSize: i < 3 ? 20 : 12, color: "var(--emp-muted)" }}>{medal}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isMe ? "font-medium" : ""}`}>{l.name} {isMe && <span className="emp-eyebrow ml-1" style={{ color: "var(--emp-accent)" }}>· You</span>}</p>
                      <p className="text-[10px]" style={{ color: "var(--emp-muted)" }}>{l.employee_code} · {(l.departments || []).join(", ")}</p>
                    </div>
                    <span className="font-editorial" style={{ color: "var(--emp-accent)" }}>{l.ets_score}</span>
                  </li>
                );
              })}
              {leaderboard.length === 0 && (
                <p className="text-xs text-center py-6" style={{ color: "var(--emp-muted)" }}>Leaderboard is empty — the first employee to log in leads it.</p>
              )}
            </ul>
            </div>
          </div>
        </div>

        {/* Profile footer */}
        <div className="mt-6 emp-glass p-6 emp-fade-in">
          <p className="emp-eyebrow mb-3 inline-flex items-center gap-1.5"><User2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Profile</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><p className="emp-eyebrow mb-1" style={{ opacity: 0.6 }}>Mobile</p><p>{me.mobile}</p></div>
            <div><p className="emp-eyebrow mb-1" style={{ opacity: 0.6 }}>Email</p><p>{me.email || "—"}</p></div>
            <div><p className="emp-eyebrow mb-1" style={{ opacity: 0.6 }}>Joined</p><p>{me.joining_date}</p></div>
            <div><p className="emp-eyebrow mb-1" style={{ opacity: 0.6 }}>Status</p><p><span className={`emp-dot emp-dot-${me.current_status || "Offline"}`} />{me.current_status || "Offline"}</p></div>
          </div>
        </div>

        {/* Change Password card · self-service */}
        <ChangePasswordCard />
      </main>
    </div>
  );
}


// ------------------------------------------------------------
// Employee self-service password reset
// ------------------------------------------------------------
function ChangePasswordCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    if (newPw === oldPw) { toast.error("New password should be different from the old one"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/employee/me/change-password`,
        { old_password: oldPw, new_password: newPw },
        authHeaders(),
      );
      toast.success("Password updated · use it the next time you sign in");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 emp-glass p-6 emp-fade-in" data-testid="emp-change-password-card">
      <p className="emp-eyebrow mb-3 inline-flex items-center gap-1.5">
        <User2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Change Password
      </p>
      <p className="text-xs mb-4" style={{ color: "var(--emp-muted)" }}>
        Reset your own password anytime. You'll need your current password to confirm it's really you.
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <input
            type={showOld ? "text" : "password"}
            className="emp-input"
            placeholder="Current password"
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            data-testid="emp-cpw-old"
            style={{ paddingRight: "36px" }}
          />
          <button type="button" onClick={() => setShowOld((s) => !s)}
            className="absolute right-1 top-1 p-1.5 opacity-60 hover:opacity-100"
            data-testid="emp-cpw-old-toggle" aria-label="Toggle visibility">
            {showOld ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
          </button>
        </div>
        <div className="relative">
          <input
            type={showNew ? "text" : "password"}
            className="emp-input"
            placeholder="New password (6+ chars)"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            data-testid="emp-cpw-new"
            style={{ paddingRight: "36px" }}
          />
          <button type="button" onClick={() => setShowNew((s) => !s)}
            className="absolute right-1 top-1 p-1.5 opacity-60 hover:opacity-100"
            data-testid="emp-cpw-new-toggle" aria-label="Toggle visibility">
            {showNew ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
          </button>
        </div>
        <input
          type={showNew ? "text" : "password"}
          className="emp-input"
          placeholder="Confirm new password"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          data-testid="emp-cpw-confirm"
        />
        <div className="sm:col-span-3 flex items-center justify-between gap-3 mt-1">
          {newPw && (
            <span className="text-[10px]" style={{ color: newPw.length >= 8 ? "#10B981" : newPw.length >= 6 ? "#C9A968" : "#B23A3A" }}>
              {newPw.length >= 12 ? "Excellent password" : newPw.length >= 8 ? "Strong password" : newPw.length >= 6 ? "OK — could be stronger" : "Too short"}
            </span>
          )}
          <button
            type="submit"
            disabled={busy || !oldPw || !newPw || !confirmPw}
            className="emp-btn-primary ml-auto disabled:opacity-50"
            data-testid="emp-cpw-submit"
          >
            {busy ? "Updating…" : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
